import { useState, useEffect, useCallback } from "react";
import { getCurrentUser, fetchAuthSession, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useDispatch } from "react-redux";
import { receiveAmplifyHubUser } from "../../actions-index";
import { redirectToLogIn } from "../../util/login";
import { useNavigate, useLocation } from "react-router-dom";
import { isUnauthenticated } from "../../util/functions";
import { authenticationFlowPaths } from "../../Routes/paths";

const CHANNELS = {
  auth: "auth",
  core: "core",
};

const EVENTS = {
  customOAuthState: "customOAuthState",
  signIn: "signIn",
  signOut: "signOut",
};

export default function useAmplifyHub() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [triggerHubAuthUseEffect, setTriggerHubAuthUseEffect] = useState(false);
  const location = useLocation();
  const pushRedirectTo = navigate;

  const navigateToRedirectPath = useCallback((path) => {
    if (path) {
      const redirectExclusions = authenticationFlowPaths.map((paths) => paths?.path);
      let redirectPath = '/';

      const splitPaths = path.split('?');
      if (!!path && path.startsWith('/') && splitPaths[0] && !redirectExclusions.includes(splitPaths[0])) {
        redirectPath = path;
      }

      if (location?.pathname !== redirectPath) {
        pushRedirectTo(redirectPath);
      }
    }
  }, [location?.pathname, pushRedirectTo]);

  const manageHubSignIn = useCallback(async () => {
    setTriggerHubAuthUseEffect(true);
    try {
      const session = await fetchAuthSession();
      const payload = session?.tokens?.idToken?.payload;
      if (payload && Object.keys(payload).length > 0) {
        dispatch(receiveAmplifyHubUser(payload));
      } else {
        dispatch(receiveAmplifyHubUser({ status: 'error' }));
      }
    } catch {
      dispatch(receiveAmplifyHubUser({ status: 'error' }));
    }
  }, [dispatch]);

  const manageHubSignOut = useCallback(() => {
    dispatch(receiveAmplifyHubUser({ status: 'cleared' }));
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const onAuthEvent = (data) => {
      const payload = data?.payload;
      if (!isMounted) {
        return;
      }
      switch (payload?.event) {
        case EVENTS.customOAuthState:
          navigateToRedirectPath(payload?.data);
          break;
        case EVENTS.signIn:
          manageHubSignIn();
          break;
        case EVENTS.signOut:
          manageHubSignOut();
          break;
        default:
          return;
      }
    };

    const unsubscribe = Hub.listen(CHANNELS.auth, onAuthEvent);

    return () => {
      unsubscribe();
      isMounted = false;
    };
  }, [triggerHubAuthUseEffect, manageHubSignIn, manageHubSignOut, navigateToRedirectPath]);

  const handleSignIn = useCallback(async (redirectPath, options = {}) => {
    const { doNotRedirect = false } = options;
    try {
      dispatch(receiveAmplifyHubUser({ status: "loading" }));
      await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session?.tokens?.idToken?.payload;
      if (payload && Object.keys(payload).length > 0) {
        dispatch(receiveAmplifyHubUser(payload));
        return true;
      } else {
        throw new Error("Improper user session");
      }
    } catch (error) {
      let authError = error;

      // On cold start, getCurrentUser can fail before Amplify hydrates tokens
      // from storage. Try fetchAuthSession as a fallback — it handles token
      // refresh and is more reliable for detecting existing sessions.
      if (doNotRedirect) {
        try {
          const session = await fetchAuthSession();
          const payload = session?.tokens?.idToken?.payload;
          if (payload && Object.keys(payload).length > 0) {
            dispatch(receiveAmplifyHubUser(payload));
            return true; // Session recovered — no redirect needed
          }
        } catch (fallbackError) {
          authError = fallbackError ?? authError;
          // fallback also failed — fall through to error handling below
        }
      }

      if (isUnauthenticated(authError)) {
        if (doNotRedirect) {
          dispatch(receiveAmplifyHubUser({ status: "error" }));
        } else {
          redirectToLogIn(redirectPath);
        }
      } else if (doNotRedirect) {
        // Keep the user on the current route on transient refresh failures.
        dispatch(receiveAmplifyHubUser({ status: "loading" }));
      } else {
        redirectToLogIn(redirectPath);
      }

      return false;
    }
  }, [dispatch]);

  const handleSignOut = useCallback(async () => {
    try {
      setTriggerHubAuthUseEffect(false);
      await signOut();
      if (location?.pathname !== "/logout") {
        navigate("/logout");
      }
    } catch (error) {
      console.error("Error signing out user ", error);
    }
  }, [location?.pathname, navigate]);

  return {
    handleSignIn,
    handleSignOut
  };
}
